import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  NO_USER,
  TEXT_FILTER_OP_LABELS,
  compactFilters,
  isActiveFilter,
  type ColumnFilterKind,
  type ColumnFilterValue,
  type ColumnFilters,
  type DistinctResult,
} from '@goyone/shared';
import { Icon } from './Icon';
import { useTeamDirectory } from './People';
import { api, qs } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { date } from '../lib/format';

/**
 * فلترة الجداول كإكسل.
 *
 * زرّ «فلتر» فوق الجدول يُظهر سهمًا في رأس كل عمود، والسهم يفتح قائمة تحته:
 * فرز، وبحث، وكل قيم العمود بعلاماتٍ وأعدادها، و«(فارغ)»، ثم «موافق».
 *
 * والقيم تأتي من الخادم (`…/distinct`) لا من الصفحة المعروضة: القوائم
 * مرقّمة هناك، وقيم الصفحة الأولى ليست قيم العمود. وتُحسب بعد شروط بقية
 * الأعمدة دون شرط العمود نفسه — كما يفعل إكسل: من فلتر «المسؤول = سارة» ثم
 * فتح «الحالة» يرى حالات عملاء سارة وحدهم.
 *
 * الشروط في الرابط (`f`) فتبقى مع التنقّل ويُرسل الرابط كما هو، والفلترة
 * نفسها في الخادم. وكل صفحة تعرّف أعمدتها بـ `FilterDef`، والخادم يعرّف
 * مقابلها — مفتاح لا يعرفه الخادم يُتجاهل هناك.
 *
 * ## الوضع المحلّي
 *
 * جداولُ تصل صفوفُها كلّها مرّةً واحدة (الأرصدة، الدليل، الخزائن، الجرد) لا
 * تحتاج خادمًا يفلترها: `FilterScope` يأخذ `rows` و`accessors` بدل
 * `distinctPath`، فتُحسب القيم وأعدادها من الصفوف، و`applyColumnFilters`
 * يفلتر ويرتّب في المتصفح — الواجهة نفسها والرقاقات نفسها والرابط نفسه.
 * و`useLocalFilterTable` يجمع ذلك لصفحةٍ في سطر.
 */

export interface FilterDef {
  key: string;
  label: string;
  kind: ColumnFilterKind;
  /** خيارات عمود من نوع enum: القيمة كما في القاعدة، والعنوان كما يُقرأ. */
  options?: readonly { value: string; label: string }[];
  /** في عمود المسؤول: هل «(فارغ)» — بلا مسؤول — خيار معقول. */
  allowNone?: boolean;
  /** يقبل الفرز من قائمته. مفتاح الفرز هو مفتاح العمود. */
  sortable?: boolean;
  /**
   * عمود نصّيّ من جدول آخر (`account.name`): لا قائمة قيم له — الخادم لا
   * يجمّع على حقل في جدول آخر — فيُفلتر بـ«يحتوي» وحدها.
   */
  noList?: boolean;
}

/* ------------------------------------------------------------------ */
/* تشغيل الفلتر: مفتاحٌ لكل صفحة في المتصفح                            */
/* ------------------------------------------------------------------ */

/*
 * هل الأسهم ظاهرة. تفضيلٌ بصري كاختيار الأعمدة، فيُحفظ في المتصفح لكل صفحة.
 * ومخزنٌ صغير لا حالة مكوّن: الزرّ في الشريط والأسهم في رأس الجدول، ولا
 * أب مشترك بينهما يملكها إلا الصفحة — وإلزام كل صفحة بتمريرها تكرارٌ عشر مرات.
 */
const listeners = new Set<() => void>();
/* `id` يفصل جدولين في صفحة واحدة (الدورات ومعاينة الإهلاك) فلا يُظهر زرُّ أحدهما أسهمَ الآخر. */
const storeKey = (id?: string) => `goyone.filterOn:${window.location.pathname}${id ? `#${id}` : ''}`;
function readOn(id?: string): boolean {
  try {
    return localStorage.getItem(storeKey(id)) === '1';
  } catch {
    return false;
  }
}
function writeOn(on: boolean, id?: string): void {
  try {
    if (on) localStorage.setItem(storeKey(id), '1');
    else localStorage.removeItem(storeKey(id));
  } catch {
    /* التخزين محجوب: يبقى التبديل لهذه الجلسة فقط. */
  }
  listeners.forEach((l) => l());
}
function useFilterOn(id?: string): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => readOn(id),
    () => false,
  );
  return [on, (v: boolean) => writeOn(v, id)];
}

/** الشروط من الرابط وإليه. */
export function useColumnFilters(
  state: Record<string, string>,
  patch: (changes: Record<string, string | undefined>) => void,
  /** اسم المعامل في الرابط — جدولٌ ثانٍ في الصفحة يأخذ اسمًا غير `f`. */
  param = 'f',
): { filters: ColumnFilters; setFilters: (next: ColumnFilters) => void } {
  const raw = state[param];
  const filters = useMemo<ColumnFilters>(() => {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as ColumnFilters) : {};
    } catch {
      return {};
    }
  }, [raw]);

  const setFilters = (next: ColumnFilters) => {
    const kept = compactFilters(next);
    patch({ [param]: kept ? JSON.stringify(kept) : undefined });
  };

  return { filters, setFilters };
}

export function activeFilterCount(defs: readonly FilterDef[], filters: ColumnFilters): number {
  return defs.filter((d) => isActiveFilter(filters[d.key])).length;
}

/** زرّ «فلتر» كزرّ إكسل: يُظهر الأسهم في رؤوس الأعمدة ويخفيها. */
export function FilterButton({
  defs,
  filters,
  onChange,
  id,
}: {
  defs: readonly FilterDef[];
  filters: ColumnFilters;
  onChange: (next: ColumnFilters) => void;
  /** يطابق `id` في `FilterScope` حين يكون في الصفحة أكثر من جدول. */
  id?: string;
}) {
  const [on, setOn] = useFilterOn(id);
  const count = activeFilterCount(defs, filters);
  const shown = on || count > 0;

  return (
    <button
      className={`btn btn-sm${shown ? ' btn-filter-on' : ''}`}
      aria-pressed={shown}
      onClick={() => {
        /* إخفاء الأسهم وفي الجدول شروطٌ يتركها تعمل بلا مكانٍ تُرى منه — فتُمسح معها. */
        if (shown && count > 0) onChange({});
        setOn(!shown);
      }}
      title={shown ? 'إخفاء أسهم الفلتر من رؤوس الأعمدة' : 'إظهار أسهم الفلتر في رؤوس الأعمدة'}
    >
      <Icon name="filter" size={14} />
      فلتر
      {count > 0 && <span className="filter-count num">{count}</span>}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* نطاق الجدول                                                          */
/* ------------------------------------------------------------------ */

/** قيمة خليّة في الوضع المحلّي كما تُقارن وتُعدّ. */
export type LocalCell = string | number | boolean | Date | null | undefined;

/** كيف تُقرأ قيمة كل عمود من الصفّ — مفتاحٌ بلا قارئ يُقرأ من `row[key]`. */
export type LocalAccessors<T> = Partial<Record<string, (row: T) => LocalCell>>;

interface Scope {
  defs: readonly FilterDef[];
  filters: ColumnFilters;
  onChange: (next: ColumnFilters) => void;
  /** مسار القيم، مثل `/crm/leads/distinct` — للوضع الخادميّ. */
  distinctPath?: string;
  /** استعلام القائمة الحالي — تُحسب القيم على ما يحصره. */
  query?: Record<string, unknown>;
  /**
   * الوضع المحلّي: صفوف الجدول كلّها (بعد البحث، قبل شروط الأعمدة) —
   * منها تُحسب القيم وأعدادها بدل الخادم.
   */
  rows?: readonly unknown[];
  accessors?: LocalAccessors<any>;
  sort?: string;
  order?: string;
  onSort?: (key: string, order: 'asc' | 'desc') => void;
  /** يطابق `id` في `FilterButton`. */
  id?: string;
}

const ScopeContext = createContext<Scope | null>(null);

/** يحيط الجدول فتعرف أسهم الرؤوس شروطها ومسارها — أو صفوفها في الوضع المحلّي. */
export function FilterScope<T = unknown>({
  children,
  ...scope
}: Omit<Scope, 'rows' | 'accessors'> & {
  rows?: readonly T[];
  accessors?: LocalAccessors<T>;
  children: ReactNode;
}) {
  return <ScopeContext.Provider value={scope as Scope}>{children}</ScopeContext.Provider>;
}

/* ------------------------------------------------------------------ */
/* الوضع المحلّي: الفلترة والفرز في المتصفح                              */
/* ------------------------------------------------------------------ */

function readCell<T>(row: T, key: string, accessors?: LocalAccessors<T>): LocalCell {
  const get = accessors?.[key];
  return get ? get(row) : ((row as Record<string, unknown>)[key] as LocalCell);
}

/** نصّ الخليّة كما يُقارن ويُعدّ — والفارغ `null`. */
function cellText(v: LocalCell): string | null {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString();
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * يومُ الخليّة (سنة-شهر-يوم) بالتوقيت المحلّي.
 * «2026-10-01» يُقرأ كما هو؛ والطابع الزمني يُحوَّل إلى يومه هنا لا في
 * غرينتش — حركةٌ في الحادية عشرة ليلًا بتوقيت القاهرة من يومها لا من الغد.
 */
function cellDay(v: LocalCell): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}(-\d{2})?$/.test(v)) return v.length === 7 ? `${v}-01` : v;
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function cellNumber(v: LocalCell): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = v instanceof Date ? v.getTime() : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** هل تحقّق الخليّة شرط عمودها — بالمعاني نفسها التي يطبّقها الخادم. */
export function matchesColumnFilter(value: LocalCell, f: ColumnFilterValue): boolean {
  switch (f.t) {
    case 'text': {
      const s = cellText(value);
      const low = s?.toLowerCase() ?? '';
      const term = (f.v ?? '').trim().toLowerCase();
      switch (f.op) {
        case 'has':
          return !term || low.includes(term);
        case 'is':
          return !term || low === term;
        case 'empty':
          return s === null;
        case 'filled':
          return s !== null;
        case 'in':
          return s === null ? !!f.blank : (f.vals ?? []).includes(s);
        case 'nin':
          return s === null ? f.blank !== false : !(f.vals ?? []).includes(s);
      }
      return true;
    }
    case 'enum':
      return f.in.includes(cellText(value) ?? '');
    case 'user':
      return f.in.includes(cellText(value) ?? NO_USER);
    case 'date': {
      const d = cellDay(value);
      if (!d) return false;
      if (f.from && d < f.from) return false;
      if (f.to && d > f.to) return false;
      return true;
    }
    case 'number': {
      const n = cellNumber(value);
      if (n === null) return false;
      /* نصف قرشٍ من التسامح: المبالغ تُعرض مقرّبة، و«≥ ١٠٠» يشمل ٩٩٫٩٩٨. */
      if (f.min !== undefined && n < f.min - 0.005) return false;
      if (f.max !== undefined && n > f.max + 0.005) return false;
      return true;
    }
  }
}

/** موازنة خليّتين غير فارغتين: الأرقام بقيمتها، والتواريخ بيومها، والنصوص بالعربية. */
function compareCells(a: LocalCell, b: LocalCell, def?: FilterDef): number {
  if (def?.kind === 'number' || (typeof a === 'number' && typeof b === 'number')) {
    return (cellNumber(a) ?? 0) - (cellNumber(b) ?? 0);
  }
  if (def?.kind === 'date') return (cellDay(a) ?? '').localeCompare(cellDay(b) ?? '');
  const ta = cellText(a) ?? '';
  const tb = cellText(b) ?? '';
  const label = (t: string) => (def?.kind === 'enum' ? (def.options?.find((o) => o.value === t)?.label ?? t) : t);
  return label(ta).localeCompare(label(tb), 'ar', { numeric: true, sensitivity: 'base' });
}

/**
 * يفلتر الصفوف بشروط الأعمدة ثم يرتّبها — نظير الخادم في الوضع المحلّي.
 * `skip` يستثني عمودًا: قيم قائمته تُحسب بعد شروط غيره لا شرطه، كإكسل.
 */
export function applyColumnFilters<T>(
  rows: readonly T[],
  filters: ColumnFilters,
  defs: readonly FilterDef[],
  accessors?: LocalAccessors<T>,
  sort?: { key?: string; order?: string },
  skip?: string,
): T[] {
  const active = defs.filter((d) => d.key !== skip && isActiveFilter(filters[d.key]));
  let out = active.length
    ? rows.filter((r) => active.every((d) => matchesColumnFilter(readCell(r, d.key, accessors), filters[d.key]!)))
    : [...rows];
  if (sort?.key) {
    const key = sort.key;
    const def = defs.find((d) => d.key === key);
    const dir = sort.order === 'asc' ? 1 : -1;
    /* الفارغ آخرًا في الاتجاهين، والمتساوي على ترتيبه الأصلي. */
    out = out
      .map((r, i) => ({ r, i, v: readCell(r, key, accessors) }))
      .sort((x, y) => {
        const bx = cellText(x.v) === null;
        const by = cellText(y.v) === null;
        if (bx || by) return bx === by ? x.i - y.i : bx ? 1 : -1;
        return compareCells(x.v, y.v, def) * dir || x.i - y.i;
      })
      .map((x) => x.r);
  }
  return out;
}

/** قيم العمود وأعدادها من الصفوف — الأكثر تكرارًا أولًا، و«(فارغ)» آخرًا، كالخادم. */
function localDistinct(scope: Scope, def: FilterDef): DistinctResult {
  const rows = applyColumnFilters(scope.rows ?? [], scope.filters, scope.defs, scope.accessors, undefined, def.key);
  const counts = new Map<string, number>();
  let blank = 0;
  for (const r of rows) {
    const v = cellText(readCell(r, def.key, scope.accessors));
    if (v === null) blank += 1;
    else counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const values: DistinctResult['values'] = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ar', { numeric: true }))
    .map(([value, count]) => ({ value, count }));
  if (blank) values.push({ value: null, count: blank });
  return { values, truncated: false };
}

/** بحثٌ نصّيّ بسيط في حقول الصفّ — بلا حساسية لحالة الأحرف. */
export function matchesSearch(term: string, ...fields: Array<string | number | null | undefined>): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return fields.some((f) => f !== null && f !== undefined && String(f).toLowerCase().includes(t));
}

/** حالة قائمةٍ في الذاكرة بشكل `useListState` — لجدولٍ في نافذة لا يكتب في رابط الصفحة. */
export function useMemoryListState(): {
  state: Record<string, string>;
  patch: (changes: Record<string, string | undefined>) => void;
} {
  const [state, setState] = useState<Record<string, string>>({});
  const patch = useCallback((changes: Record<string, string | undefined>) => {
    setState((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(changes)) {
        if (v === undefined || v === '') delete next[k];
        else next[k] = v;
      }
      return next;
    });
  }, []);
  return { state, patch };
}

/**
 * جدولٌ محلّيّ كامل في نداءٍ واحد: الشروط والفرز من الرابط (`f`، `sort`،
 * `order` — أو `{param}`، `{param}Sort`، `{param}Order` لجدولٍ ثانٍ في
 * الصفحة)، والصفوف بعد البحث والشروط والفرز، وخصائص الزرّ والرقاقات
 * والنطاق جاهزةٌ تُنشر عليها.
 */
export function useLocalFilterTable<T>({
  rows,
  defs,
  accessors,
  state,
  patch,
  param = 'f',
  search,
  defaultSort,
}: {
  rows: readonly T[] | undefined;
  defs: readonly FilterDef[];
  accessors?: LocalAccessors<T>;
  state: Record<string, string>;
  patch: (changes: Record<string, string | undefined>) => void;
  param?: string;
  /** شرط البحث الحرّ على الصفّ، إن كان للجدول بحث. */
  search?: (row: T) => boolean;
  /** ترتيبٌ حين لا يختار المستخدم — وإلا فترتيب الخادم كما هو. */
  defaultSort?: { key: string; order: 'asc' | 'desc' };
}) {
  const { filters, setFilters } = useColumnFilters(state, patch, param);
  const sortKey = param === 'f' ? 'sort' : `${param}Sort`;
  const orderKey = param === 'f' ? 'order' : `${param}Order`;
  const sort = state[sortKey] ?? defaultSort?.key;
  const order = state[sortKey] ? state[orderKey] : defaultSort?.order;
  const id = param === 'f' ? undefined : param;

  const base = useMemo(() => (search ? (rows ?? []).filter(search) : [...(rows ?? [])]), [rows, search]);
  const shown = useMemo(
    () => applyColumnFilters(base, filters, defs, accessors, { key: sort, order }),
    [base, filters, defs, accessors, sort, order],
  );
  const onSort = (key: string, o: 'asc' | 'desc') => patch({ [sortKey]: key, [orderKey]: o });

  return {
    /** الصفوف المعروضة: بعد البحث والشروط والفرز. */
    rows: shown,
    /** الصفوف بعد البحث وقبل شروط الأعمدة. */
    base,
    filters,
    setFilters,
    sort,
    order,
    /** عددُ الأعمدة المشروطة. */
    active: activeFilterCount(defs, filters),
    button: { defs, filters, onChange: setFilters, id },
    chips: { defs, filters, onChange: setFilters },
    scope: { defs, filters, onChange: setFilters, rows: base, accessors, sort, order, onSort, id },
  };
}

/** السهم في رأس العمود. لا يُرسم لعمود بلا فلتر أو والفلتر مطفأ. */
export function HeaderFilter({ colKey }: { colKey: string }) {
  const scope = useContext(ScopeContext);
  const [on] = useFilterOn(scope?.id);
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  if (!scope) return null;
  const def = scope.defs.find((d) => d.key === colKey);
  const count = activeFilterCount(scope.defs, scope.filters);
  if (!def || !(on || count > 0)) return null;

  const active = isActiveFilter(scope.filters[def.key]);
  const sorted = scope.sort === def.key;

  return (
    <>
      <button
        ref={anchor}
        type="button"
        draggable={false}
        className={`th-filter${active ? ' is-active' : ''}`}
        aria-label={`فلتر ${def.label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {active ? (
          <Icon name="filter" size={12} />
        ) : sorted ? (
          <Icon name={scope.order === 'asc' ? 'arrowUp' : 'arrowDown'} size={12} />
        ) : (
          <Icon name="chevronDown" size={12} />
        )}
      </button>
      {open && <ColumnMenu def={def} scope={scope} anchor={anchor} onClose={() => setOpen(false)} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* قائمة العمود                                                          */
/* ------------------------------------------------------------------ */

const MENU_WIDTH = 290;
const GAP = 6;

/** مفتاحٌ داخلي لـ«(فارغ)» في مجموعة العلامات. */
const BLANK = '\u0000blank';

function ColumnMenu({
  def,
  scope,
  anchor,
  onClose,
}: {
  def: FilterDef;
  scope: Scope;
  anchor: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  } | null>(null);

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(MENU_WIDTH, window.innerWidth - GAP * 2);
    /* الصفحة من اليمين: حافّة القائمة اليمنى تحت حافّة السهم اليمنى. */
    let left = r.right - width;
    left = Math.max(GAP, Math.min(left, window.innerWidth - width - GAP));
    /*
     * تحت السهم إن اتّسع، وإلا فوقه — رأس جدولٍ في أسفل الشاشة كان يدفع
     * القائمة خارجها. والارتفاع يُقصّ على ما يتّسع فتُمرَّر داخل نفسها.
     */
    const below = window.innerHeight - r.bottom - GAP * 2;
    const above = r.top - GAP * 2;
    if (below >= 380 || below >= above) {
      setPos({ top: r.bottom + GAP, left, maxHeight: below });
    } else {
      setPos({ bottom: window.innerHeight - r.top + GAP, left, maxHeight: above });
    }
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchor.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    /* القائمة مرساة إلى السهم لحظة الفتح؛ تمرير الصفحة يتركها معلّقةً بعيدًا عنه. */
    const onScroll = (e: Event) => {
      if (panel.current?.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [anchor, onClose]);

  const current = scope.filters[def.key];
  const apply = (v: ColumnFilterValue | undefined) => {
    const next = { ...scope.filters };
    if (v) next[def.key] = v;
    else delete next[def.key];
    scope.onChange(next);
    onClose();
  };

  if (!pos) return null;

  return createPortal(
    <div
      ref={panel}
      className="menu-panel col-menu"
      role="dialog"
      aria-label={`فلتر ${def.label}`}
      style={{
        top: pos.top,
        bottom: pos.bottom,
        left: pos.left,
        width: Math.min(MENU_WIDTH, window.innerWidth - GAP * 2),
        maxHeight: pos.maxHeight,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {def.sortable && scope.onSort && (
        <>
          <button type="button" className="menu-item" onClick={() => (scope.onSort!(def.key, 'asc'), onClose())}>
            <Icon name="arrowUp" size={14} />
            <span>فرز تصاعدي</span>
          </button>
          <button type="button" className="menu-item" onClick={() => (scope.onSort!(def.key, 'desc'), onClose())}>
            <Icon name="arrowDown" size={14} />
            <span>فرز تنازلي</span>
          </button>
        </>
      )}
      <button
        type="button"
        className="menu-item menu-item-sep"
        disabled={!isActiveFilter(current)}
        onClick={() => apply(undefined)}
      >
        <Icon name="eraser" size={14} />
        <span>مسح الفلتر من «{def.label}»</span>
      </button>

      <div className="col-menu-body">
        {def.kind === 'date' || def.kind === 'number' ? (
          <RangeEditor def={def} current={current} onApply={apply} onCancel={onClose} />
        ) : def.noList ? (
          <ContainsEditor current={current} onApply={apply} onCancel={onClose} />
        ) : (
          <ValueList def={def} scope={scope} current={current} onApply={apply} onCancel={onClose} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function MenuFoot({ onOk, onCancel, okDisabled }: { onOk: () => void; onCancel: () => void; okDisabled?: boolean }) {
  return (
    <div className="col-menu-foot">
      <button type="button" className="btn btn-sm btn-primary" onClick={onOk} disabled={okDisabled}>
        موافق
      </button>
      <button type="button" className="btn btn-sm" onClick={onCancel}>
        إلغاء
      </button>
    </div>
  );
}

/** «يحتوي على» — لعمود من جدول آخر لا قائمة قيم له. */
function ContainsEditor({
  current,
  onApply,
  onCancel,
}: {
  current: ColumnFilterValue | undefined;
  onApply: (v: ColumnFilterValue | undefined) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(current?.t === 'text' && current.op === 'has' ? (current.v ?? '') : '');
  const ok = () => onApply(text.trim() ? { t: 'text', op: 'has', v: text.trim() } : undefined);
  return (
    <>
      <label className="tiny mute">يحتوي على</label>
      <input
        className="input"
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && ok()}
      />
      <MenuFoot onOk={ok} onCancel={onCancel} />
    </>
  );
}

/** من … إلى — للتواريخ والأرقام. */
function RangeEditor({
  def,
  current,
  onApply,
  onCancel,
}: {
  def: FilterDef;
  current: ColumnFilterValue | undefined;
  onApply: (v: ColumnFilterValue | undefined) => void;
  onCancel: () => void;
}) {
  const isDate = def.kind === 'date';
  const [from, setFrom] = useState(
    current?.t === 'date' ? (current.from ?? '') : current?.t === 'number' ? String(current.min ?? '') : '',
  );
  const [to, setTo] = useState(
    current?.t === 'date' ? (current.to ?? '') : current?.t === 'number' ? String(current.max ?? '') : '',
  );
  const ok = () => {
    if (isDate) {
      onApply(from || to ? { t: 'date', from: from || undefined, to: to || undefined } : undefined);
    } else {
      const min = from === '' ? undefined : Number(from);
      const max = to === '' ? undefined : Number(to);
      onApply(min !== undefined || max !== undefined ? { t: 'number', min, max } : undefined);
    }
  };
  return (
    <>
      <label className="tiny mute">من</label>
      <input type={isDate ? 'date' : 'number'} className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
      <label className="tiny mute">إلى</label>
      <input type={isDate ? 'date' : 'number'} className="input" value={to} onChange={(e) => setTo(e.target.value)} />
      <MenuFoot onOk={ok} onCancel={onCancel} />
    </>
  );
}

/**
 * قائمة القيم بعلاماتها — قلب الفلتر.
 *
 * تبدأ كلها معلَّمة إن لم يكن على العمود شرط. ومن البحث: ما يظهر هو ما
 * يُعلَّم، و«موافق» يحصر الجدول فيه — كإكسل حين يُكتب في خانة البحث.
 */
function ValueList({
  def,
  scope,
  current,
  onApply,
  onCancel,
}: {
  def: FilterDef;
  scope: Scope;
  current: ColumnFilterValue | undefined;
  onApply: (v: ColumnFilterValue | undefined) => void;
  onCancel: () => void;
}) {
  const team = useTeamDirectory();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  /* في الوضع المحلّي القيم من الصفوف، والبحث فيها هنا كلّه. */
  const local = !scope.distinctPath;
  /* بحث الخادم للنصّ وحده؛ قوائم الحالات والفريق قصيرة فتُبحث هنا. */
  const serverSearch = def.kind === 'text' && !local ? debounced.trim() : '';

  const { page: _p, pageSize: _s, ...listQuery } = (scope.query ?? {}) as Record<string, unknown>;
  const remote = useQuery({
    queryKey: ['distinct', scope.distinctPath, def.key, listQuery, serverSearch],
    queryFn: () =>
      api<DistinctResult>(`${scope.distinctPath}${qs({ ...listQuery, col: def.key, dq: serverSearch || undefined })}`),
    enabled: !local,
  });
  const localData = useMemo(() => (local ? localDistinct(scope, def) : undefined), [local, scope, def]);
  const values = local
    ? { data: localData, isPending: false, isError: false }
    : { data: remote.data, isPending: remote.isPending, isError: remote.isError };

  const labelOf = useCallback(
    (v: string | null): string => {
      if (v === null) return '(فارغ)';
      if (def.kind === 'enum') return def.options?.find((o) => o.value === v)?.label ?? v;
      if (def.kind === 'user') return team.byId.get(v)?.name ?? 'عضو سابق';
      return v;
    },
    [def, team.byId],
  );

  const keyOf = (v: string | null) => (v === null ? BLANK : v);

  /* القيم بعد بحث المتصفح (للحالات والفريق) — والنصّ بحثه في الخادم. */
  const rows = useMemo(() => {
    const all = values.data?.values ?? [];
    const term = search.trim().toLowerCase();
    if (!term || (def.kind === 'text' && !local)) return all;
    return all.filter((r) => labelOf(r.value).toLowerCase().includes(term));
  }, [values.data, search, def.kind, labelOf, local]);

  const searching = search.trim().length > 0;

  /*
   * العلامات: «مستبعد» لا «مختار».
   * الحالة الابتدائية «الكل» تُمثَّل بمجموعة فارغة، فتحميل قيمٍ جديدة (بحث،
   * أو قيم أكثر) لا يحتاج إعادة حساب ما عُلِّم.
   */
  const [excluded, setExcluded] = useState<Set<string> | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  /* من شرطٍ قائم: يُبنى «المستبعد» حين تصل القيم. */
  useEffect(() => {
    if (excluded !== null || !values.data) return;
    const all = values.data.values.map((r) => keyOf(r.value));
    if (!current || !isActiveFilter(current)) {
      setExcluded(new Set());
      return;
    }
    let keep: Set<string> | null = null;
    if (current.t === 'text' && current.op === 'in') {
      keep = new Set([...(current.vals ?? []), ...(current.blank ? [BLANK] : [])]);
    } else if (current.t === 'text' && current.op === 'nin') {
      setExcluded(new Set([...(current.vals ?? []), ...(current.blank === false ? [BLANK] : [])]));
      return;
    } else if (current.t === 'enum' || current.t === 'user') {
      keep = new Set(current.in.map((v) => (v === NO_USER ? BLANK : v)));
    }
    setExcluded(keep ? new Set(all.filter((k) => !keep!.has(k))) : new Set());
  }, [values.data, current, excluded]);

  /* البحث يبدأ بنتائجه كلها معلَّمة، كإكسل. */
  useEffect(() => {
    if (searching) setPicked(new Set(rows.map((r) => keyOf(r.value))));
  }, [searching, rows]);

  const isChecked = (k: string) => (searching ? picked.has(k) : !(excluded ?? new Set()).has(k));
  const toggle = (k: string) => {
    if (searching) {
      const next = new Set(picked);
      next.has(k) ? next.delete(k) : next.add(k);
      setPicked(next);
    } else {
      const next = new Set(excluded ?? []);
      next.has(k) ? next.delete(k) : next.add(k);
      setExcluded(next);
    }
  };
  const visibleKeys = rows.map((r) => keyOf(r.value));
  const allChecked = visibleKeys.every(isChecked);
  const someChecked = visibleKeys.some(isChecked);
  const toggleAll = () => {
    if (searching) setPicked(allChecked ? new Set() : new Set(visibleKeys));
    else setExcluded(allChecked ? new Set(visibleKeys) : new Set());
  };

  const build = (): ColumnFilterValue | undefined => {
    const truncated = values.data?.truncated ?? false;
    const chosen = searching ? [...picked] : visibleKeys.filter((k) => !(excluded ?? new Set()).has(k));
    const blankChosen = chosen.includes(BLANK);
    const real = chosen.filter((k) => k !== BLANK);

    /* لا شيء مستبعد ولا بحث: لا شرط. */
    if (!searching && !(excluded ?? new Set()).size) return undefined;

    if (def.kind === 'text') {
      /*
       * «الكل إلا» حين تكون القائمة مقصوصة — أو، محلّيًّا، حين المستبعد أقلّ
       * من المختار: إزالة علامةٍ من خمسمئة اسم لا تكتب ٤٩٩ اسمًا في الرابط.
       */
      const fewerExcluded = local && (excluded ?? new Set()).size < chosen.length;
      if (!searching && (truncated || fewerExcluded)) {
        const ex = [...(excluded ?? new Set())];
        return { t: 'text', op: 'nin', vals: ex.filter((k) => k !== BLANK), blank: !ex.includes(BLANK) };
      }
      return { t: 'text', op: 'in', vals: real, blank: blankChosen };
    }
    /* «موافق» معطّل بلا علامة، فالقائمة الفارغة هنا لا تقع. */
    if (def.kind === 'user') {
      const ins = [...real, ...(blankChosen ? [NO_USER] : [])];
      return ins.length ? { t: 'user', in: ins } : undefined;
    }
    return real.length ? { t: 'enum', in: real } : undefined;
  };

  const nothing = !searching ? !someChecked && visibleKeys.length > 0 : picked.size === 0;

  return (
    <>
      <div className="col-menu-search">
        <Icon name="search" size={14} />
        <input
          className="input"
          placeholder="بحث"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      <div className="col-menu-list" role="group" aria-label={`قيم ${def.label}`}>
        {values.isPending ? (
          <div className="tiny mute col-menu-note">جارٍ تحميل القيم…</div>
        ) : values.isError ? (
          <div className="tiny col-menu-note" style={{ color: 'var(--danger)' }}>
            تعذّر تحميل القيم.
          </div>
        ) : rows.length === 0 ? (
          <div className="tiny mute col-menu-note">لا قيم مطابقة.</div>
        ) : (
          <>
            <label className="col-menu-row">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked && !allChecked;
                }}
                onChange={toggleAll}
              />
              <span>{searching ? '(تحديد كل نتائج البحث)' : '(تحديد الكل)'}</span>
            </label>
            {rows.map((r) => {
              const k = keyOf(r.value);
              return (
                <label key={k} className="col-menu-row">
                  <input type="checkbox" checked={isChecked(k)} onChange={() => toggle(k)} />
                  <span className="col-menu-val">{labelOf(r.value)}</span>
                  <span className="tiny mute num">{r.count}</span>
                </label>
              );
            })}
          </>
        )}
      </div>
      {values.data?.truncated && (
        <div className="tiny mute col-menu-note">
          تظهر أكثر {values.data.values.length} قيمة تكرارًا — ابحث للوصول إلى غيرها.
        </div>
      )}
      <MenuFoot onOk={() => onApply(build())} onCancel={onCancel} okDisabled={nothing || values.isPending} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* رقاقات الشروط                                                         */
/* ------------------------------------------------------------------ */

/** وصف الشرط في رقاقة: «المسؤول: سارة، دعاء». */
function describe(def: FilterDef, f: ColumnFilterValue, names: Map<string, string>): string {
  const label = (v: string) =>
    def.kind === 'enum' ? (def.options?.find((o) => o.value === v)?.label ?? v) : v;
  const list = (vs: string[], max = 3) =>
    vs.length > max ? `${vs.slice(0, max).join('، ')} و${vs.length - max} غيرها` : vs.join('، ');

  switch (f.t) {
    case 'text': {
      if (f.op === 'in') {
        const vs = [...(f.vals ?? []), ...(f.blank ? ['(فارغ)'] : [])];
        return vs.length ? list(vs) : 'لا شيء';
      }
      if (f.op === 'nin') {
        const vs = [...(f.vals ?? []), ...(f.blank === false ? ['(فارغ)'] : [])];
        return `الكل إلا ${list(vs)}`;
      }
      return f.op === 'empty' || f.op === 'filled'
        ? TEXT_FILTER_OP_LABELS[f.op]
        : `${TEXT_FILTER_OP_LABELS[f.op]} «${f.v}»`;
    }
    case 'enum':
      return list(f.in.map(label));
    case 'user':
      return list(f.in.map((v) => (v === NO_USER ? '(فارغ)' : (names.get(v) ?? 'عضو سابق'))));
    case 'date':
      return f.from && f.to
        ? `${date(f.from)} — ${date(f.to)}`
        : f.from
          ? `من ${date(f.from)}`
          : `حتى ${date(f.to!)}`;
    case 'number':
      return f.min !== undefined && f.max !== undefined
        ? `${f.min} — ${f.max}`
        : f.min !== undefined
          ? `≥ ${f.min}`
          : `≤ ${f.max}`;
  }
}

/**
 * الفلاتر النشطة سطورًا تُقرأ — «الحالة: مدفوعة، ملغاة» — كما في الشرائح،
 * لملاحظات ملفّ التصدير. عمود المسؤول يحتاج أسماء الفريق، فتُمرَّر إن وُجد.
 */
export function describeFilters(
  defs: readonly FilterDef[],
  filters: ColumnFilters,
  names: Map<string, string> = new Map(),
): string[] {
  return defs
    .filter((d) => isActiveFilter(filters[d.key]))
    .map((d) => `${d.label}: ${describe(d, filters[d.key]!, names)}`);
}

export function FilterChips({
  defs,
  filters,
  onChange,
}: {
  defs: readonly FilterDef[];
  filters: ColumnFilters;
  onChange: (next: ColumnFilters) => void;
}) {
  const team = useTeamDirectory();
  const names = useMemo(() => new Map(team.members.map((m) => [m.userId, m.name])), [team.members]);
  const active = defs.filter((d) => isActiveFilter(filters[d.key]));
  if (!active.length) return null;

  return (
    <div className="filter-chips">
      {active.map((d) => (
        <span key={d.key} className="filter-chip">
          <span className="strong">{d.label}:</span> {describe(d, filters[d.key]!, names)}
          <button
            type="button"
            className="filter-chip-x"
            aria-label={`إزالة فلتر ${d.label}`}
            onClick={() => {
              const next = { ...filters };
              delete next[d.key];
              onChange(next);
            }}
          >
            <Icon name="x" size={12} />
          </button>
        </span>
      ))}
      {active.length > 1 && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange({})}>
          مسح كل الفلاتر
        </button>
      )}
    </div>
  );
}
